import {
  ConfirmationDialog,
  DataGrid,
  Dialog,
  downloadFile,
  FileUploader,
  Form,
  formatNumber,
  StatusBadge,
  useNotifications,
  type FilterDef,
  type Severity
} from '@ijoin/components';
import {
  BulkUpsertAdvisorBody,
  getGetAdminAdvisoryOrgAdvisorsQueryKey,
  usePostAdminAdvisoryOrgAdvisorsBulk
} from '@ijoin/hooks';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';

import { groupBy } from 'lodash';
import Papa from 'papaparse';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useToggle } from 'usehooks-ts';

import { ApiError } from '../types';
import { BulkAdvisorRowDialog } from './BulkAdvisorRowDialog';
import {
  ADVISOR_UPLOAD_COLUMNS,
  parseAdvisorUploadRows,
  toUploadBatches,
  UploadRow,
  UploadStatus
} from './bulkAdvisorUpload.utils';

const STATUS_SEVERITY: Record<UploadStatus, Severity> = {
  created: 'success',
  failed: 'error',
  invalid: 'error',
  partial: 'warning',
  pending: 'info',
  updated: 'success',
  valid: 'info'
};

export const BulkAdvisorUploadDialog: FC<{
  advisoryOrganizationId: string;
  advisoryOrganizationName: string;
  onClose: () => void;
}> = props => {
  const notifications = useNotifications();
  const queryClient = useQueryClient();
  const methods = useForm<{ file: File | null }>({
    defaultValues: { file: null }
  });

  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [phase, setPhase] = useState<'done' | 'idle' | 'uploading'>('idle');
  const [progress, setProgress] = useState({ batch: 0, batches: 0, sent: 0 });
  const [selected, setSelected] = useState<{
    row: UploadRow;
    tab: 'messages' | 'plans';
  }>();
  const [isConfirmOpen, toggleConfirmOpen] = useToggle();

  const bulkUpsert = usePostAdminAdvisoryOrgAdvisorsBulk<ApiError>({
    mutation: {
      onError: error =>
        notifications.error(error.message || 'Failed to upload advisors')
    }
  });

  const onFileSelect = (file: File | FileList | null) => {
    if (!(file instanceof File)) {
      setFileName('');
      setRows([]);
      return;
    }

    Papa.parse<Record<string, string>>(file, {
      complete: results => {
        setFileName(file.name);
        setRows(parseAdvisorUploadRows(results.data));
        setPhase('idle');
      },
      header: true,
      skipEmptyLines: 'greedy',
      transformHeader: header =>
        ADVISOR_UPLOAD_COLUMNS.find(
          column => column.header.toLowerCase() === header.trim().toLowerCase()
        )?.key ?? header
    });
  };

  const onRemoveFile = () => {
    methods.setValue('file', null);
    setFileName('');
    setRows([]);
    setPhase('idle');
  };

  const onDownloadTemplate = () =>
    downloadFile({
      content: Papa.unparse({
        data: [ADVISOR_UPLOAD_COLUMNS.map(column => column.example)],
        fields: ADVISOR_UPLOAD_COLUMNS.map(column => column.header)
      }),
      fileName: 'advisor-upload-template.csv',
      type: 'text/csv'
    });

  const onDownloadResults = () =>
    downloadFile({
      content: Papa.unparse({
        data: rows.map(row => [
          ...ADVISOR_UPLOAD_COLUMNS.map(column => {
            const value = row[column.key];

            if (Array.isArray(value)) return value.join('|');
            if (typeof value === 'boolean') return value ? 'Y' : 'N';

            return value ?? '';
          }),
          row.status,
          [...row.errors, ...row.warnings].join('; ')
        ]),
        fields: [
          ...ADVISOR_UPLOAD_COLUMNS.map(column => column.header),
          'Status',
          'Messages'
        ]
      }),
      fileName: `${fileName.replace(/\.csv$/i, '')}-results.csv`,
      type: 'text/csv'
    });

  const onUpload = async () => {
    toggleConfirmOpen();
    setPhase('uploading');
    setRows(current => current.map(row => ({ ...row, status: 'pending' })));

    const batches = toUploadBatches(rows);
    setProgress({ batch: 0, batches: batches.length, sent: 0 });

    for (const [index, batch] of batches.entries()) {
      let response;

      try {
        response = await bulkUpsert.mutateAsync({
          advisoryOrganizationId: props.advisoryOrganizationId,
          data: {
            advisors: batch.map(
              ({ errors, id, plans, status, warnings, ...body }) =>
                body as BulkUpsertAdvisorBody
            )
          }
        });
      } catch {
        /* the hook's onError already told the user; put the unsent rows back so the upload can be retried */
        setPhase('idle');
        setRows(current =>
          current.map(row =>
            row.status === 'pending' ? { ...row, status: 'valid' } : row
          )
        );
        return;
      }

      const resultsByUsername = new Map(
        response.results.map(result => [result.username.toLowerCase(), result])
      );
      setRows(current =>
        current.map(row => {
          const result = resultsByUsername.get(row.username.toLowerCase());

          return result
            ? {
                ...row,
                errors: result.errors,
                plans: result.plans,
                status: result.status,
                warnings: result.warnings
              }
            : row;
        })
      );
      setProgress(current => ({
        batch: index + 1,
        batches: batches.length,
        sent: current.sent + batch.length
      }));
    }

    queryClient.invalidateQueries({
      queryKey: getGetAdminAdvisoryOrgAdvisorsQueryKey(
        props.advisoryOrganizationId
      )
    });
    setPhase('done');
  };

  const onCloseRow = () => setSelected(undefined);

  const rowsByStatus = groupBy(rows, 'status');
  const invalidCount = rowsByStatus.invalid?.length ?? 0;
  const failedCount = rowsByStatus.failed?.length ?? 0;
  const partialCount = rowsByStatus.partial?.length ?? 0;
  const planRefs = rows.reduce(
    (sum, row) =>
      sum + (row.hasAllPlans ? 0 : (row.externalPlanIds?.length ?? 0)),
    0
  );
  const plansAdded = rows.reduce(
    (sum, row) => sum + (row.plans?.added ?? 0),
    0
  );
  const plansRemoved = rows.reduce(
    (sum, row) => sum + (row.plans?.removed ?? 0),
    0
  );
  const hasIssues = !!(failedCount || partialCount);
  const resultSeverity: Severity =
    rows.length && failedCount === rows.length
      ? 'error'
      : hasIssues
        ? 'warning'
        : 'success';
  const canUpload = phase === 'idle' && !!rows.length && !invalidCount;

  const columns: GridColDef<UploadRow>[] = [
    ...ADVISOR_UPLOAD_COLUMNS.filter(column => column.type === 'text').map(
      (column): GridColDef<UploadRow> => ({
        field: column.key,
        headerName: column.header,
        width: column.key === 'email' || column.key === 'username' ? 210 : 130
      })
    ),
    ...ADVISOR_UPLOAD_COLUMNS.filter(column => column.type === 'boolean').map(
      (column): GridColDef<UploadRow> => ({
        display: 'flex',
        field: column.key,
        headerName: column.header,
        renderCell: params =>
          params.value !== undefined && (
            <StatusBadge
              data-testid={`${column.key}-${params.row.id}`}
              severity={params.value ? 'success' : 'info'}>
              {params.value ? 'Yes' : 'No'}
            </StatusBadge>
          ),
        width: 150
      })
    ),
    {
      align: 'right',
      display: 'flex',
      field: 'externalPlanIds',
      headerAlign: 'right',
      headerName: 'Plans',
      renderCell: params =>
        params.row.hasAllPlans ? (
          'All'
        ) : (
          <Button
            data-testid={`plans-${params.row.id}`}
            onClick={() => setSelected({ row: params.row, tab: 'plans' })}
            variant='text'>
            {formatNumber(params.row.externalPlanIds?.length ?? 0)}
          </Button>
        ),
      sortable: false,
      width: 100
    },
    {
      display: 'flex',
      field: 'status',
      headerName: 'Status',
      renderCell: params => (
        <StatusBadge
          data-testid={`status-${params.row.id}`}
          severity={STATUS_SEVERITY[params.row.status]}>
          {params.row.status}
        </StatusBadge>
      ),
      width: 110
    },
    {
      align: 'center',
      disableColumnMenu: true,
      display: 'flex',
      field: 'actions',
      filterable: false,
      headerName: '',
      renderCell: params =>
        !!(params.row.errors.length || params.row.warnings.length) && (
          <IconButton
            aria-label={
              params.row.errors.length ? 'View errors' : 'View warnings'
            }
            data-testid={`details-${params.row.id}`}
            onClick={() => setSelected({ row: params.row, tab: 'messages' })}>
            {params.row.errors.length ? (
              <ErrorOutlineIcon color='error' />
            ) : (
              <WarningAmberIcon color='warning' />
            )}
          </IconButton>
        ),
      sortable: false,
      width: 60
    }
  ];

  const filterDefs: FilterDef[] = [
    ...ADVISOR_UPLOAD_COLUMNS.filter(
      column => column.type === 'text' && column.key !== 'phone'
    ).map((column): FilterDef => ({
      label: column.header,
      name: column.key,
      type: 'text'
    })),
    ...ADVISOR_UPLOAD_COLUMNS.filter(column => column.type === 'boolean').map(
      (column): FilterDef => ({
        label: column.header,
        name: column.key,
        options: [
          { label: 'Yes', value: 'true' },
          { label: 'No', value: 'false' }
        ],
        type: 'select'
      })
    ),
    { label: 'Plan ID', name: 'externalPlanIds', type: 'text' },
    {
      label: 'Status',
      name: 'status',
      options: Object.keys(STATUS_SEVERITY).map(status => ({
        label: status[0].toUpperCase() + status.slice(1),
        value: status
      })),
      type: 'multiSelect'
    }
  ];

  return (
    <Dialog
      data-testid='bulkAdvisorUpload'
      fullHeight
      fullWidth
      isOpen
      locked={phase === 'uploading'}
      maxWidth='xl'
      onClose={props.onClose}>
      <DialogTitle variant='h3'>
        <Stack
          alignItems='center'
          direction='row'
          justifyContent='space-between'>
          Bulk Upload Advisors
          <IconButton
            aria-label='Close'
            data-testid='closeBulkAdvisorUpload'
            disabled={phase === 'uploading'}
            onClick={props.onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex' }}>
        <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
          <Stack
            alignItems='center'
            direction='row'
            justifyContent='space-between'>
            <Button
              data-testid='downloadAdvisorTemplate'
              onClick={onDownloadTemplate}
              startIcon={<FileDownloadOutlinedIcon />}
              variant='text'>
              Download Template
            </Button>
            {phase === 'done' && (
              <Button
                data-testid='downloadAdvisorResults'
                onClick={onDownloadResults}
                startIcon={<FileDownloadOutlinedIcon />}
                variant='text'>
                Download Results
              </Button>
            )}
          </Stack>

          <Accordion disableGutters variant='outlined'>
            <AccordionSummary
              data-testid='columnReference'
              expandIcon={<ExpandMoreIcon />}>
              <Typography variant='subtitle1'>Column reference</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Column</TableCell>
                    <TableCell>Rule</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ADVISOR_UPLOAD_COLUMNS.map(column => (
                    <TableRow key={column.key}>
                      <TableCell>{column.header}</TableCell>
                      <TableCell>{column.rule}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography color='text.secondary' sx={{ mt: 1 }} variant='body2'>
                Blank cells keep the current value on existing advisors and use
                the default on new ones. Repeat a username on another line to
                add more plan ids.
              </Typography>
            </AccordionDetails>
          </Accordion>

          {!fileName && (
            <Form methods={methods}>
              <FileUploader
                accept='.csv,text/csv'
                caption='CSV only, up to 250 MB. Nothing is sent until you click Upload.'
                label='Drag and drop a CSV file here, or click to upload'
                maxSize={250}
                name='file'
                onFileSelect={onFileSelect}
                readAs='file'
                variant='dropzone'
              />
            </Form>
          )}

          {!!fileName && phase === 'idle' && (
            <Alert
              action={
                <Button
                  color='inherit'
                  data-testid='removeAdvisorFile'
                  onClick={onRemoveFile}>
                  Remove file
                </Button>
              }
              data-testid='parsedAlert'
              severity={invalidCount ? 'warning' : 'info'}>
              <AlertTitle>
                {fileName}: {formatNumber(rows.length)} advisors
              </AlertTitle>
              {formatNumber(rowsByStatus.valid?.length ?? 0)} valid ·{' '}
              {formatNumber(invalidCount)} invalid · {formatNumber(planRefs)}{' '}
              plan refs.{' '}
              {invalidCount
                ? 'Fix the invalid rows in the file and upload it again.'
                : 'Review the rows below, then click Upload.'}
            </Alert>
          )}

          {phase === 'uploading' && (
            <Alert data-testid='uploadingAlert' severity='info'>
              <AlertTitle>Uploading advisors</AlertTitle>
              Batch {progress.batch} of {progress.batches} ·{' '}
              {formatNumber(progress.sent)} of {formatNumber(rows.length)}{' '}
              advisors. Keep this dialog open until the upload finishes.
              <LinearProgress
                sx={{ mt: 1 }}
                value={
                  progress.batches
                    ? (progress.batch / progress.batches) * 100
                    : 0
                }
                variant='determinate'
              />
            </Alert>
          )}

          {phase === 'done' && (
            <Alert
              action={
                <Button
                  color='inherit'
                  data-testid='dismissAdvisorResults'
                  onClick={onRemoveFile}>
                  Dismiss
                </Button>
              }
              data-testid='resultsAlert'
              severity={resultSeverity}>
              <AlertTitle>
                {hasIssues ? 'Upload finished with issues' : 'Upload finished'}
              </AlertTitle>
              {formatNumber(rowsByStatus.created?.length ?? 0)} created ·{' '}
              {formatNumber(rowsByStatus.updated?.length ?? 0)} updated ·{' '}
              {formatNumber(partialCount)} partial · {formatNumber(failedCount)}{' '}
              failed · {formatNumber(plansAdded)} plan refs added ·{' '}
              {formatNumber(plansRemoved)} removed
            </Alert>
          )}

          <DataGrid
            autoHeight={false}
            columns={columns}
            disableRowSelectionOnClick
            filterDefs={filterDefs}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 100 } }
            }}
            pageSizeOptions={[50, 100, 200]}
            pinnedColumns={{ right: ['externalPlanIds', 'status', 'actions'] }}
            rows={rows}
            showToolbar
            slotProps={{
              noRowsOverlay: { message: 'Upload a CSV to preview advisors' },
              root: {
                'data-component': 'dataGrid',
                'data-testid': 'bulkAdvisors'
              }
            }}
            sx={{ flex: 1, minHeight: 0 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          data-testid='cancelBulkAdvisorUpload'
          disabled={phase === 'uploading'}
          onClick={props.onClose}
          variant='outlined'>
          {phase === 'done' ? 'Close' : 'Cancel'}
        </Button>
        <Button
          data-testid='uploadBulkAdvisors'
          disabled={!canUpload}
          onClick={toggleConfirmOpen}
          variant='contained'>
          Upload
        </Button>
      </DialogActions>

      {isConfirmOpen && (
        <ConfirmationDialog
          confirmText='Upload'
          data-testid='confirmBulkAdvisorUpload'
          isOpen={isConfirmOpen}
          message={`${formatNumber(rows.length)} advisors will be created or updated in ${props.advisoryOrganizationName} and their plan access set to match the file. Rows are sent in batches; re-uploading the same file is safe.`}
          onCancel={toggleConfirmOpen}
          onConfirm={onUpload}
          title='Upload advisors?'
        />
      )}

      {selected && (
        <BulkAdvisorRowDialog
          initialTab={selected.tab}
          onClose={onCloseRow}
          row={selected.row}
        />
      )}
    </Dialog>
  );
};

BulkAdvisorUploadDialog.displayName = 'BulkAdvisorUploadDialog';
